/**
 * Set — Prodotto/i abbinato/i come SET OBBLIGATORIO (es. specchio)
 *
 * Parallelo a corpo-incasso.js ma OBBLIGATORIO: il/i prodotto/i del set sono
 * sempre inclusi (nessun radio, nessuna deselezione) e vengono aggiunti al
 * carrello come righe abbinate quando il prodotto padre viene aggiunto.
 * Property riga figlio: { _abbinato_a: <titolo padre>, _tipo_abbinato: 'Set',
 * _ambiente? }. La quantità (1 all'add, poi allineata al padre) è gestita
 * dalla riconciliazione generica in quote-manager.js.
 *
 * @version 1.0.0
 */

(function() {
  'use strict';

  // Singleton: la barra sticky del tema (floated-form) ri-renderizza i blocchi
  // della scheda prodotto, compreso lo snippet che include QUESTO script. Senza
  // guardia lo script gira due volte, con due listener `variant:add`
  // indipendenti (ciascuno col proprio `isAdding`, quindi la guardia
  // single-flight non li vede a vicenda): scattano insieme, entrambi trovano il
  // carrello ancora senza la riga figlia e aggiungono 1 pezzo ciascuno →
  // Shopify fonde le righe → quantità 2. Succedeva a ogni aggiunta quando la
  // selezione è automatica (set obbligatorio, sempre incluso).
  if (window.__bongioSetInit) return;
  window.__bongioSetInit = true;

  const SELECTORS = {
    section: '#product-set-section',
    productsContainer: '#product-set-products',
    productForm: 'form[action*="/cart/add"]'
  };

  // code -> { product, card }
  const itemsMap = new Map();

  let showPrice = true;
  let selectable = true;
  let includedLabel = 'Incluso';

  // Single-flight guard: `variant:add` può essere dispatchato due volte per un
  // solo submit (buy-buttons + notification-modal). Senza guardia si creerebbero
  // righe duplicate del set.
  let isAdding = false;

  // ==========================================================================
  // INIT
  // ==========================================================================

  function init() {
    const section = document.querySelector(SELECTORS.section);
    if (!section) return;

    const raw = section.dataset.setCodes;
    if (!raw) return;

    const codes = parseCodes(raw);
    if (codes.length === 0) return;

    showPrice = section.dataset.showPrice !== 'false';
    selectable = section.dataset.selectable !== 'false';
    includedLabel = section.dataset.includedLabel || 'Incluso';

    section.style.display = 'block';
    fetchAndRender(codes);

    // SET OBBLIGATORIO: il listener è SEMPRE attivo, a differenza del corpo
    // incasso (dove la selezione è riservata agli acquirenti). Chiunque riesca
    // ad aggiungere il prodotto padre al carrello deve ricevere anche il
    // prodotto del set. `showPrice` continua a nascondere il prezzo in card
    // ai non-acquirenti.
    document.addEventListener('variant:add', onVariantAdd);
  }

  // ==========================================================================
  // PARSING
  // ==========================================================================

  function parseCodes(raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map(c => String(c).trim()).filter(Boolean);
      }
    } catch (e) { /* not JSON */ }
    return raw.split(',').map(c => c.trim()).filter(Boolean);
  }

  // ==========================================================================
  // FETCHING
  // ==========================================================================

  async function fetchAndRender(codes) {
    const container = document.querySelector(SELECTORS.productsContainer);
    if (!container) return;

    const products = [];
    for (const code of codes) {
      const product = await fetchProduct(code);
      products.push({ code, product });
    }

    container.innerHTML = '';

    if (products.length === 0) {
      container.innerHTML = '<div class="product-set__empty">Nessun prodotto del set trovato</div>';
      return;
    }

    for (const { code, product } of products) {
      const card = createCard(code, product);
      itemsMap.set(code, { product, card });
      container.appendChild(card);
    }

    // Il set è sempre incluso: scriviamo la property riepilogo sulla riga padre.
    updateFormInputs();
  }

  async function fetchProduct(code) {
    console.log('[Set] Fetching product for code:', code);

    const handle = code.toLowerCase().replace(/\s+/g, '-');
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (res.ok) {
        const product = await res.json();
        console.log('[Set] Found via direct handle:', code, '→ price:', product.price);
        return product;
      }
    } catch (e) { /* continue */ }

    try {
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(code)}&resources[type]=product&resources[limit]=5`;
      const res = await fetch(searchUrl);
      if (res.ok) {
        const data = await res.json();
        const results = data.resources?.results?.products || [];
        let match = results.find(p =>
          p.title.trim() === code || p.title.trim().toLowerCase() === code.toLowerCase()
        );
        if (!match) match = results.find(p => p.title.trim().toLowerCase().startsWith(code.toLowerCase()));
        if (!match) match = results.find(p => p.title.trim().toLowerCase().includes(code.toLowerCase()));
        if (!match && results.length > 0) match = results[0];
        if (match) {
          try {
            const fullRes = await fetch(`/products/${match.handle}.js`);
            if (fullRes.ok) {
              const product = await fullRes.json();
              console.log('[Set] Full product loaded:', product.title, '→ price:', product.price);
              return product;
            }
          } catch (e) { /* fallback below */ }
          return normalizeSearchResult(match);
        }
      }
    } catch (e) {
      console.warn('[Set] Search failed for:', code, e);
    }

    console.warn('[Set] Product not found for code:', code);
    return null;
  }

  function normalizeSearchResult(result) {
    if (!result) return result;
    if (typeof result.price === 'string') {
      const cleaned = result.price.replace(/[^\d.,]/g, '').replace(',', '.');
      const amount = parseFloat(cleaned);
      if (!isNaN(amount)) result.price = Math.round(amount * 100);
    }
    return result;
  }

  // ==========================================================================
  // RENDERING — card bloccata "Incluso", nessun radio
  // ==========================================================================

  function stripHtml(html) {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    } catch (e) {
      return '';
    }
  }

  function createCard(code, product) {
    const card = document.createElement('div');
    card.className = 'product-set__card';
    card.dataset.code = code;

    // Immagine
    const imgUrl = product ? getImage(product) : null;
    if (imgUrl) {
      const imageWrap = document.createElement('div');
      imageWrap.className = 'product-set__image';
      const img = document.createElement('img');
      img.src = resizeImage(imgUrl, 120);
      img.alt = product.title || code;
      img.loading = 'lazy';
      img.width = 50;
      img.height = 50;
      imageWrap.appendChild(img);
      card.appendChild(imageWrap);
    }

    // Info
    const info = document.createElement('div');
    info.className = 'product-set__info';

    const name = document.createElement('div');
    name.className = 'product-set__name';
    name.textContent = product ? (product.title || code) : code;
    info.appendChild(name);

    const descText = product ? stripHtml(product.description || '') : '';
    const descEl = document.createElement('div');
    descEl.className = 'product-set__desc';
    descEl.textContent = descText || ('Art. ' + code);
    info.appendChild(descEl);

    card.appendChild(info);

    // Prezzo (solo acquirenti). != null così anche prezzo 0 è mostrato.
    // Prefisso "+" per chiarire che il prezzo del set SI SOMMA a quello del
    // prodotto (non è compreso nel prezzo mostrato sopra).
    const price = showPrice && product ? getPrice(product) : null;
    if (price != null) {
      const priceEl = document.createElement('div');
      priceEl.className = 'product-set__price';
      priceEl.textContent = '+ ' + formatPrice(price);
      card.appendChild(priceEl);
    }

    // Badge "Incluso" (segnale obbligatorietà) — visibile a tutti: il set è
    // parte del prodotto, non un'opzione.
    const badge = document.createElement('span');
    badge.className = 'product-set__badge';
    badge.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
    badge.appendChild(document.createTextNode(includedLabel));
    card.appendChild(badge);

    return card;
  }

  // ==========================================================================
  // FORM — riepilogo set sulla riga padre (cosmetico)
  // ==========================================================================

  function updateFormInputs() {
    const form = document.querySelector(SELECTORS.productForm);
    if (!form) return;
    form.querySelectorAll('input[name^="properties[Set"]').forEach(el => el.remove());

    const parts = [];
    itemsMap.forEach((item, code) => {
      const title = item.product ? (item.product.title || code) : code;
      parts.push(code + ' - ' + title);
    });
    if (parts.length === 0) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[Set]';
    input.value = parts.join(', ');
    form.appendChild(input);
  }

  // ==========================================================================
  // ADD TO CART — sempre, per ogni prodotto del set (obbligatorio)
  // ==========================================================================

  async function onVariantAdd(event) {
    if (itemsMap.size === 0) return;

    if (isAdding) {
      console.log('[Set] Add already in flight, skipping duplicate dispatch');
      return;
    }
    isAdding = true;

    try {
      const abbinatoA = document.querySelector('.product__title')?.textContent?.trim() || '';

      let amb = '';
      try { amb = localStorage.getItem('bongio:ambiente_attivo') || ''; } catch (_) {}

      // Carrello attuale per il dedupe (una sola lettura per tutto il set).
      let cartItems = [];
      try {
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          cartItems = Array.isArray(cart && cart.items) ? cart.items : [];
        }
      } catch (e) {
        console.warn('[Set] dedupe check failed, proceeding', e);
      }

      let addedAny = false;
      for (const [code, item] of itemsMap) {
        if (!item || !item.product) continue;
        const variantId = getFirstVariantId(item.product);
        if (!variantId) {
          console.warn('[Set] No variant ID for:', code);
          continue;
        }

        // Dedupe su (variant + _abbinato_a + _ambiente): stesso set in stanze
        // diverse resta su righe separate.
        const dup = cartItems.some(it =>
          it.variant_id === variantId &&
          it.properties &&
          String(it.properties._abbinato_a || '') === abbinatoA &&
          String(it.properties._ambiente || '') === amb
        );
        if (dup) {
          console.log('[Set] Prodotto set già presente per', abbinatoA, '— skip', code);
          continue;
        }

        const body = JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: Object.assign(
              { '_abbinato_a': abbinatoA, '_tipo_abbinato': 'Set' },
              amb ? { '_ambiente': amb } : {}
            )
          }]
        });

        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: body
        });
        const data = await addRes.json().catch(() => ({}));
        if (data.status) {
          console.warn('[Set] Failed to add:', code, data.description);
          continue;
        }
        console.log('[Set] Added to cart:', code);
        addedAny = true;
      }

      if (addedAny) refreshCart();
    } catch (err) {
      console.error('[Set] Add to cart error:', err);
    } finally {
      isAdding = false;
    }
  }

  function refreshCart() {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (!cart) return;

    const sectionsToRender = cart.getSectionsToRender
      ? cart.getSectionsToRender().map(s => s.id)
      : [];

    if (sectionsToRender.length > 0) {
      fetch(`${window.location.pathname}?sections=${sectionsToRender.join(',')}`)
        .then(res => res.json())
        .then(sections => {
          if (cart.getSectionsToRender) {
            cart.getSectionsToRender().forEach(section => {
              const html = sections[section.id];
              if (html) {
                const el = document.getElementById(section.id);
                if (el) {
                  el.innerHTML = new DOMParser()
                    .parseFromString(html, 'text/html')
                    .getElementById(section.id)?.innerHTML || html;
                }
              }
            });
          }
        })
        .catch(err => console.warn('[Set] Cart refresh error:', err));
    }

    fetch('/cart.js')
      .then(res => res.json())
      .then(cartData => {
        const bubbles = document.querySelectorAll('.cart-count-bubble span, [data-cart-count]');
        bubbles.forEach(el => { el.textContent = cartData.item_count; });
      })
      .catch(() => {});
  }

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  function getImage(product) {
    if (product.featured_image) {
      return typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image.src || product.featured_image;
    }
    if (product.image) return product.image;
    if (product.images && product.images.length > 0) {
      const first = product.images[0];
      return typeof first === 'string' ? first : first.src || first;
    }
    return null;
  }

  function resizeImage(url, width) {
    if (!url) return url;
    return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, `_${width}x.$1$2`);
  }

  function getPrice(product) {
    if (typeof product.price === 'number') return product.price;
    if (typeof product.price === 'string') {
      const cleaned = product.price.replace(/[^\d.,]/g, '').replace(',', '.');
      const amount = parseFloat(cleaned);
      if (!isNaN(amount)) return Math.round(amount * 100);
    }
    if (typeof product.price_min === 'number') return product.price_min;
    return null;
  }

  function getFirstVariantId(product) {
    if (product.variants && product.variants.length > 0) {
      return product.variants[0].id;
    }
    return null;
  }

  function formatPrice(price) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(price);
    }
    const amount = price / 100;
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  }

  // ==========================================================================
  // BOOTSTRAP
  // ==========================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
